/**
 * Honest recipe prices: selling price is a £ field, GP% is a percent.
 * Save stays disabled with an inline reason — not a toast behind the modal.
 */

import { formatGbp } from './formatGbp'
import {
  firstRecipeHasShoppingListIngredient,
  hasRecipeSellingPrice,
} from './growthEngineFirstRecipe'

export const RECIPE_SELLING_PRICE_LABEL = 'Selling price'
export const RECIPE_SELLING_PRICE_HINT =
  'What you charge for this dish. This is a £ price, not GP%.'

export const RECIPE_MARGIN_EX_VAT_LABEL = 'Margin (ex VAT)'
export const RECIPE_MARGIN_EX_VAT_HINT =
  'Selling price without VAT. Not a percent.'

export const RECIPE_GP_PERCENT_LABEL = 'GP%'
export const RECIPE_GP_PERCENT_HINT =
  'Gross profit as a percent — not a £ price. Do not type 8.50 here.'
export const RECIPE_GP_PERCENT_PLACEHOLDER = 'e.g. 70'

function hasShoppingListOrNamedIngredient(ingredients = []) {
  return (
    Array.isArray(ingredients) &&
    ingredients.some(
      ingredient =>
        ingredient &&
        (ingredient.productId ||
          ingredient._id ||
          ingredient.venueProductId ||
          ingredient.customIngredient ||
          ingredient.product),
    )
  )
}

function recipeHasAnyIngredient({
  ingredients = [],
  customProducts = [],
  recipeIngredients = [],
} = {}) {
  if (hasShoppingListOrNamedIngredient(ingredients)) return true
  if (Array.isArray(customProducts) && customProducts.some(Boolean)) return true
  if (Array.isArray(recipeIngredients) && recipeIngredients.some(Boolean)) {
    return true
  }
  return false
}

function needPhrase(need) {
  if (need === 'ingredient') return 'an ingredient'
  return `a ${need}`
}

export function formatRecipeSaveDisabledReason(needs = []) {
  const list = Array.isArray(needs) ? needs.filter(Boolean) : []
  if (!list.length) return null
  if (list.length === 1) return `Needs ${needPhrase(list[0])}`
  if (list.length === 2) {
    return `Needs ${needPhrase(list[0])} and ${needPhrase(list[1])}`
  }
  return `Needs ${needPhrase(list[0])}, ${needPhrase(
    list[1],
  )}, and ${needPhrase(list[2])}`
}

/**
 * Why Save is disabled: name, ingredient, selling price.
 * Slim first still requires a shopping-list product (same as canSaveSlimFirstRecipe).
 */
export function getRecipeSaveDisabledReason({
  title,
  ingredients,
  customProducts,
  recipeIngredients,
  sellingPrice,
  isSlimFirstRecipe = false,
} = {}) {
  const needs = []
  if (!String(title || '').trim()) needs.push('name')
  const hasIngredient = isSlimFirstRecipe
    ? firstRecipeHasShoppingListIngredient(ingredients)
    : recipeHasAnyIngredient({
        ingredients,
        customProducts,
        recipeIngredients,
      })
  if (!hasIngredient) needs.push('ingredient')
  if (!hasRecipeSellingPrice(sellingPrice)) needs.push('selling price')
  return formatRecipeSaveDisabledReason(needs)
}

/** Persist a number, never NaN, so the recipe list cannot print £NaN. */
export function toFiniteRecipeCost(value, fallback = 0) {
  const n = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

/** List/detail cost. Missing cost is an em dash — not £NaN. */
export function formatRecipeCostPerPortionLabel(value) {
  const n = toFiniteRecipeCost(value, Number.NaN)
  if (!Number.isFinite(n)) return '—'
  return formatGbp(n)
}
